import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { CompaniesService } from '@services/companies.service';
import { CompanyModel } from '@data-layer/models/companies';
import { ReplaySubject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { AuthService } from '@app/modules/auth/services';
import { IHeaderItem } from '@app/modules/app-layout/interfaces';
import { AppLayoutService } from '@app/modules/app-layout/app-layout.service';
import { enumUserBetaFlag } from '@data-layer/enums/user';

@Component({
  selector: 'app-account-settings-wrapper',
  templateUrl: './account-settings-wrapper.component.html',
  styleUrls: ['./account-settings-wrapper.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountSettingsWrapperComponent implements OnInit, OnDestroy {
  public companies: CompanyModel[];

  public isAnOrganizationActiveRoute: boolean = false;
  public isCompanyActiveRoute: boolean = false;
  public isPoaPurchaseRoute: boolean = false;
  public companyTabLink: string;
  public ACCOUNT_SETTINGS_URL: string = '/companies/account-settings/';

  public enumUserBetaFlag: typeof enumUserBetaFlag = enumUserBetaFlag;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private companiesService: CompaniesService,
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private appLayoutService: AppLayoutService,
    private cdr: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    const queryParams = this.route.snapshot.queryParams;

    this.isAnOrganizationActiveRoute = this.isIncludedInOrganizationPage(this.router.url.slice(this.ACCOUNT_SETTINGS_URL.length));
    this.isCompanyActiveRoute = this.isIncludedInCompanyPage(this.router.url.slice(this.ACCOUNT_SETTINGS_URL.length));
    this.isPoaPurchaseRoute = this.checkIsPoaPurchaseRoute(this.router.url);
    this.updateDashboardHeader(this.router.url.slice(this.ACCOUNT_SETTINGS_URL.length));

    this.companies = this.companiesService.getCompanies();
    this.companyTabLink =
      this.companies.length > 1
        ? `${this.ACCOUNT_SETTINGS_URL}/company-list`
        : `${this.ACCOUNT_SETTINGS_URL}/company-view/${this.companiesService.getCompany().id}`;

    // #7855 (skip redirect on POA purchase route)
    if (
      this.companies?.length <= 1 &&
      !this.companies?.[0]?.businessName &&
      !this.isCreateCompany &&
      !this.isOrganization &&
      !this.isPoaPurchaseRoute
    ) {
      this.router.navigate([this.companyTabLink], { queryParams });
    }
    this.cdr.markForCheck();

    this.router.events
      .pipe(
        takeUntil(this.destroy$),
        filter((event) => event instanceof NavigationEnd),
      )
      .subscribe((event: NavigationEnd) => {
        this.isAnOrganizationActiveRoute = this.isIncludedInOrganizationPage(event.url.slice(this.ACCOUNT_SETTINGS_URL.length));
        this.isCompanyActiveRoute = this.isIncludedInCompanyPage(event.url.slice(this.ACCOUNT_SETTINGS_URL.length));
        this.isPoaPurchaseRoute = this.checkIsPoaPurchaseRoute(event.url);
        this.updateDashboardHeader(this.router.url.slice(this.ACCOUNT_SETTINGS_URL.length));
      });

    this.companiesService.companiesChange$.pipe(takeUntil(this.destroy$)).subscribe((companiesChanges) => {
      this.companies = companiesChanges.current;
      this.companyTabLink =
        this.companies.length > 1
          ? `${this.ACCOUNT_SETTINGS_URL}/company-list`
          : `${this.ACCOUNT_SETTINGS_URL}/company-view/${this.companiesService.getCompany().id}`;
      this.cdr.markForCheck();
    });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.appLayoutService.contentHeader$.next(null);
  }

  private updateDashboardHeader(param?: string): void {
    if (this.isPoaPurchaseRoute) {
      // Let the child POA page control the full breadcrumb
      return;
    }
    const title: IHeaderItem[] = [];
    title.push({
      key: 'MENU.ACCOUNT_SETTINGS',
      link: null,
    });
    title.push({
      key: this.mapTitles(param),
      link: null,
    });
    this.appLayoutService.contentHeader$.next({ title, mobileTitle: title });
  }

  private isIncludedInOrganizationPage(url: string): boolean {
    return url.includes('organization') || url.includes('organization-view');
  }

  private isIncludedInCompanyPage(url: string): boolean {
    return url.includes('create') || url.includes('company-list') || url.includes('company-view');
  }

  // TODO: fix redirection after enter to create company page to company view
  private get isCreateCompany(): boolean {
    return this.router.url.slice(this.ACCOUNT_SETTINGS_URL.length).includes('create');
  }

  private get isOrganization(): boolean {
    return this.router.url.slice(this.ACCOUNT_SETTINGS_URL.length).includes('organization');
  }

  private mapTitles(key: string): string {
    if (key.includes('poa-purchase')) {
      return 'COMPANY.DIGITAL-SIGNATURE.POA_PURCHASE.BREADCRUMB';
    }
    if (key.includes('company-view')) {
      return 'LABELS.IMPORTER_OF_RECORD';
    }
    if (key.includes('company-list')) {
      return 'LABELS.IMPORTERS_OF_RECORD';
    }
    if (key.includes('security')) {
      return 'LABELS.SECURITY';
    }
    if (key.includes('user-details')) {
      return 'LABELS.USER_DETAILS';
    }
    if (key.includes('organization')) {
      return 'LABELS.ORGANIZATION';
    }
    if (key.includes('general')) {
      return 'LABELS.GENERAL';
    }
    if (key.includes('create')) {
      return 'LABELS.CREATE_IMPORTER_OF_RECORD';
    }
  }

  private checkIsPoaPurchaseRoute(url: string): boolean {
    return url.includes('/company-view/') && url.includes('/poa-purchase');
  }
}
